import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Shield, Users, Lock, Save, Loader2, RefreshCw } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { getRoleDisplayName } from '../utils/roleUtils';
import type { UserRole } from '../types';

interface Permission {
  id: string;
  name: string;
  description: string;
  category: string;
}

interface RolePermissions {
  role: string;
  permissions: string[];
}

interface UserPermission {
  userId: string;
  userEmail: string;
  permissions: string[];
}

interface PermissionsManagementProps {
  accessToken: string;
}

const AVAILABLE_PERMISSIONS: Permission[] = [
  // Company Management
  { id: 'manage_all_companies', name: 'Manage All Companies', description: 'Can view and edit any company', category: 'Company Management' },
  { id: 'manage_own_companies', name: 'Manage Own Companies', description: 'Can only manage associated companies', category: 'Company Management' },
  { id: 'view_all_companies', name: 'View All Companies', description: 'Can view all companies but not edit', category: 'Company Management' },
  { id: 'create_company', name: 'Create Companies', description: 'Can create new companies', category: 'Company Management' },
  { id: 'purge_company_data', name: 'Purge Company Data', description: 'Can delete all project data while preserving company and SOWs', category: 'Company Management' },
  
  // User Management
  { id: 'manage_all_users', name: 'Manage All Users', description: 'Can manage users across all companies', category: 'User Management' },
  { id: 'manage_company_users', name: 'Manage Company Users', description: 'Can manage users in their company', category: 'User Management' },
  { id: 'invite_users', name: 'Invite Users', description: 'Can invite new users', category: 'User Management' },
  { id: 'disable_user_login', name: 'Disable User Login', description: 'Can disable user login access', category: 'User Management' },
  { id: 'assign_roles', name: 'Assign Roles', description: 'Can assign and change user roles', category: 'User Management' },
  
  // SOW Management
  { id: 'manage_sows', name: 'Manage SOWs', description: 'Can create and edit SOWs', category: 'SOW Management' },
  { id: 'view_sows', name: 'View SOWs', description: 'Can view SOWs and change orders', category: 'SOW Management' },
  { id: 'approve_sows', name: 'Approve SOWs', description: 'Can approve and mark SOW status', category: 'SOW Management' },
  
  // Project/Space Management
  { id: 'manage_projects', name: 'Manage Jira Spaces', description: 'Can create and edit Jira spaces', category: 'Project Management' },
  { id: 'manage_workflows', name: 'Manage Workflows', description: 'Can create and edit workflows', category: 'Project Management' },
  { id: 'manage_work_types', name: 'Manage Work Types', description: 'Can create and edit work types', category: 'Project Management' },
  { id: 'manage_teams', name: 'Manage Teams', description: 'Can create and edit teams', category: 'Project Management' },
  { id: 'manage_request_types', name: 'Manage Request Types', description: 'Can create and edit request types', category: 'Project Management' },
  
  // Implementation
  { id: 'mark_ready_implementation', name: 'Mark Ready for Implementation', description: 'Can mark items ready for implementation', category: 'Implementation' },
  { id: 'reopen_requests', name: 'Reopen Requests', description: 'Can reopen items marked ready', category: 'Implementation' },
  { id: 'mark_implemented', name: 'Mark Implemented', description: 'Can mark items as implemented', category: 'Implementation' },
  
  // Audit & Monitoring
  { id: 'view_audit_log', name: 'View Audit Log', description: 'Can view comprehensive audit logs', category: 'Audit & Monitoring' },
];

const DEFAULT_ROLE_PERMISSIONS: Record<string, string[]> = {
  systemAdmin: AVAILABLE_PERMISSIONS.map(p => p.id), // All permissions
  cfAdmin: [
    'manage_all_companies',
    'view_all_companies',
    'create_company',
    'purge_company_data',
    'manage_all_users',
    'invite_users',
    'disable_user_login',
    'assign_roles',
    'manage_sows',
    'view_sows',
    'approve_sows',
    'manage_projects',
    'manage_workflows',
    'manage_work_types',
    'manage_teams',
    'manage_request_types',
    'mark_ready_implementation',
    'reopen_requests',
    'mark_implemented',
    'view_audit_log',
  ],
  billingAdmin: [
    'view_all_companies',
    'view_sows',
    'view_audit_log',
  ],
  bdRep: [
    'view_all_companies',
    'manage_sows',
    'view_sows',
    'approve_sows',
    'view_audit_log',
  ],
  consultant: [
    'manage_own_companies',
    'view_all_companies',
    'purge_company_data',
    'manage_company_users',
    'invite_users',
    'disable_user_login',
    'manage_sows',
    'approve_sows',
    'manage_projects',
    'manage_workflows',
    'manage_work_types',
    'manage_teams',
    'manage_request_types',
    'reopen_requests',
    'mark_implemented',
    'view_audit_log',
  ],
  companyAdmin: [
    'purge_company_data',
    'manage_company_users',
    'invite_users',
    'manage_projects',
    'manage_workflows',
    'manage_work_types',
    'manage_teams',
    'manage_request_types',
    'mark_ready_implementation',
  ],
  user: [
    'manage_projects',
    'manage_workflows',
    'manage_work_types',
    'manage_teams',
    'manage_request_types',
  ],
};

export function PermissionsManagement({ accessToken }: PermissionsManagementProps) {
  console.log('[PermissionsManagement] Component mounting/rendering');
  const [activeTab, setActiveTab] = useState<'role' | 'user'>('role');
  const [selectedRole, setSelectedRole] = useState<string>('systemAdmin'); // NEW: Only show one role at a time
  const [rolePermissions, setRolePermissions] = useState<RolePermissions[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [originalRolePermissions, setOriginalRolePermissions] = useState<RolePermissions[]>([]);
  const [originalUserPermissions, setOriginalUserPermissions] = useState<UserPermission[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [lastLoaded, setLastLoaded] = useState<Date | null>(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // Load permissions once on mount
  useEffect(() => {
    if (!permissionsLoaded && !isLoadingPermissions) {
      console.log('[PermissionsManagement] useEffect triggered - loading permissions');
      loadPermissions();
    }
  }, [permissionsLoaded, isLoadingPermissions]); // Prevent duplicate loads

  // Load users only when user tab is accessed
  useEffect(() => {
    if (activeTab === 'user' && !usersLoaded && !isLoadingUsers) {
      console.log('[PermissionsManagement] User tab accessed - loading users');
      loadUsers();
    }
  }, [activeTab, usersLoaded, isLoadingUsers]); // Prevent duplicate loads

  const loadPermissions = async () => {
    // Prevent duplicate loads
    if (isLoadingPermissions) {
      console.log('[FRONTEND] ⚠️ Permissions already loading, skipping duplicate request');
      return;
    }
    
    console.log('[FRONTEND] Starting to load permissions...');
    const startTime = Date.now();
    setIsLoadingPermissions(true);
    setLoading(true);
    try {
      const fetchStart = Date.now();
      console.log('[FRONTEND] Initiating fetch request...');
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/permissions`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      console.log(`[FRONTEND] Permissions fetch completed in ${Date.now() - fetchStart}ms, status: ${response.status}`);

      console.log('[FRONTEND] Response received, parsing JSON...');
      if (response.ok) {
        const parseStart = Date.now();
        const data = await response.json();
        console.log(`[FRONTEND] JSON parsed in ${Date.now() - parseStart}ms`);
        
        // PERFORMANCE: Use setTimeout to prevent UI freeze when setting large state
        console.log('[FRONTEND] Setting permissions state...');
        const setStateStart = Date.now();
        
        // Only use server data if it has actual role permissions, otherwise use defaults
        if (data.rolePermissions && data.rolePermissions.length > 0) {
          setRolePermissions(data.rolePermissions);
          setUserPermissions(data.userPermissions || []);
          setOriginalRolePermissions(JSON.parse(JSON.stringify(data.rolePermissions)));
          setOriginalUserPermissions(JSON.parse(JSON.stringify(data.userPermissions || [])));
        } else {
          // Initialize with defaults if no permissions are saved
          const defaultRoles = Object.entries(DEFAULT_ROLE_PERMISSIONS).map(([role, permissions]) => ({ role, permissions }));
          setRolePermissions(defaultRoles);
          setUserPermissions([]);
          setOriginalRolePermissions(JSON.parse(JSON.stringify(defaultRoles)));
          setOriginalUserPermissions([]);
        }
        console.log(`[FRONTEND] State updated in ${Date.now() - setStateStart}ms`);
      } else {
        // Initialize with defaults
        const defaultRoles = Object.entries(DEFAULT_ROLE_PERMISSIONS).map(([role, permissions]) => ({ role, permissions }));
        setRolePermissions(defaultRoles);
        setUserPermissions([]);
        setOriginalRolePermissions(JSON.parse(JSON.stringify(defaultRoles)));
        setOriginalUserPermissions([]);
      }
      setPermissionsLoaded(true);
      setLastLoaded(new Date());
      console.log(`[FRONTEND] Total permissions load time: ${Date.now() - startTime}ms`);
    } catch (err) {
      console.error('[FRONTEND] Error loading permissions:', err);
      const defaultRoles = Object.entries(DEFAULT_ROLE_PERMISSIONS).map(([role, permissions]) => ({ role, permissions }));
      setRolePermissions(defaultRoles);
      setUserPermissions([]);
      setOriginalRolePermissions(JSON.parse(JSON.stringify(defaultRoles)));
      setOriginalUserPermissions([]);
    } finally {
      setLoading(false);
      setIsLoadingPermissions(false);
    }
  };

  const loadUsers = async () => {
    // Prevent duplicate loads
    if (isLoadingUsers) {
      console.log('[FRONTEND] ⚠️ Users already loading, skipping duplicate request');
      return;
    }
    
    console.log('[FRONTEND] Starting to load users for permissions...');
    const startTime = Date.now();
    setIsLoadingUsers(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/users`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      console.log(`[FRONTEND] Users fetch completed in ${Date.now() - startTime}ms, status: ${response.status}`);

      if (response.ok) {
        const data = await response.json();
        // The endpoint returns an array directly, not wrapped in {users: []}
        setUsers(Array.isArray(data) ? data : (data.users || []));
        setUsersLoaded(true);
      } else {
        console.error('[FRONTEND] Failed to load users:', response.status, await response.text());
      }
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const savePermissions = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/permissions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            rolePermissions,
            userPermissions,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save permissions');
      }

      // Update the original state after successful save
      setOriginalRolePermissions(JSON.parse(JSON.stringify(rolePermissions)));
      setOriginalUserPermissions(JSON.parse(JSON.stringify(userPermissions)));
      
      setSuccess('Permissions saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error saving permissions:', err);
      setError(err.message || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const toggleRolePermission = (role: string, permissionId: string) => {
    setRolePermissions(prev => prev.map(rp => {
      if (rp.role === role) {
        const hasPermission = rp.permissions.includes(permissionId);
        return {
          ...rp,
          permissions: hasPermission
            ? rp.permissions.filter(p => p !== permissionId)
            : [...rp.permissions, permissionId]
        };
      }
      return rp;
    }));
  };

  const toggleUserPermission = (userId: string, permissionId: string) => {
    setUserPermissions(prev => {
      const existing = prev.find(up => up.userId === userId);
      if (existing) {
        const hasPermission = existing.permissions.includes(permissionId);
        return prev.map(up => 
          up.userId === userId
            ? {
                ...up,
                permissions: hasPermission
                  ? up.permissions.filter(p => p !== permissionId)
                  : [...up.permissions, permissionId]
              }
            : up
        );
      } else {
        const user = users.find(u => u.id === userId);
        return [...prev, {
          userId,
          userEmail: user?.email || '',
          permissions: [permissionId]
        }];
      }
    });
  };

  // Labels come from the taxonomy (docs/PATHKIT_ROLE_TAXONOMY.md) via roleUtils.
  const roleLabel = (role: string) => getRoleDisplayName(role as UserRole);

  // PERFORMANCE: Memoize expensive calculations
  const groupedPermissions = useMemo(() => {
    const grouped: Record<string, Permission[]> = {};
    AVAILABLE_PERMISSIONS.forEach(perm => {
      if (!grouped[perm.category]) {
        grouped[perm.category] = [];
      }
      grouped[perm.category].push(perm);
    });
    return grouped;
  }, []); // Only calculate once, permissions are static

  // PERFORMANCE: Memoize role permissions lookups
  const rolePermissionsMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    rolePermissions.forEach(rp => {
      map.set(rp.role, new Set(rp.permissions));
    });
    return map;
  }, [rolePermissions]);

  // PERFORMANCE: Memoize user permissions lookups
  const userPermissionsMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    userPermissions.forEach(up => {
      map.set(up.userId, new Set(up.permissions));
    });
    return map;
  }, [userPermissions]);

  const hasRolePermission = (role: string, permissionId: string) => {
    return rolePermissionsMap.get(role)?.has(permissionId) || false;
  };

  const hasUserPermission = (userId: string, permissionId: string) => {
    return userPermissionsMap.get(userId)?.has(permissionId) || false;
  };

  // Detect if there are unsaved changes
  const hasChanges = useMemo(() => {
    // Deep comparison for role permissions
    const roleChanged = JSON.stringify(rolePermissions) !== JSON.stringify(originalRolePermissions);
    // Deep comparison for user permissions
    const userChanged = JSON.stringify(userPermissions) !== JSON.stringify(originalUserPermissions);
    return roleChanged || userChanged;
  }, [rolePermissions, userPermissions, originalRolePermissions, originalUserPermissions]);

  if (loading && !permissionsLoaded) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Loading permissions configuration...</p>
          <p className="text-sm text-muted-foreground mt-2">This should only take a moment</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {success && (
        <Alert>
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Permissions Management
              </CardTitle>
              <CardDescription>
                Configure role-based and user-specific permissions
                {lastLoaded && (
                  <span className="ml-2 text-xs">
                    • Loaded {lastLoaded.toLocaleTimeString()}
                  </span>
                )}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPermissionsLoaded(false);
                  loadPermissions();
                }}
                disabled={loading}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button onClick={savePermissions} disabled={saving || !hasChanges}>
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'role' | 'user')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="role">Role-Based Permissions</TabsTrigger>
              <TabsTrigger value="user">User-Specific Permissions</TabsTrigger>
            </TabsList>

            <TabsContent value="role" className="space-y-6 mt-6">
              <Alert>
                <AlertDescription>
                  Role-based permissions define the default access level for each role. These can be overridden by user-specific permissions.
                </AlertDescription>
              </Alert>

              {/* PERFORMANCE FIX: Only show one role at a time instead of all 4 */}
              <div className="mb-4">
                <Label htmlFor="role-select">Select Role</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger id="role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="systemAdmin">System Admin</SelectItem>
                    <SelectItem value="cfAdmin">Firm Admin</SelectItem>
                    <SelectItem value="billingAdmin">Billing Admin</SelectItem>
                    <SelectItem value="bdRep">Business Development Rep</SelectItem>
                    <SelectItem value="consultant">Consultant</SelectItem>
                    <SelectItem value="companyAdmin">Client Engagement Lead</SelectItem>
                    <SelectItem value="user">Client Team Member</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    {roleLabel(selectedRole)}
                  </CardTitle>
                  {selectedRole === 'systemAdmin' && (
                    <CardDescription>
                      System Administrators always have all permissions (cannot be changed)
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-6">
                  {Object.entries(groupedPermissions).map(([category, permissions]) => (
                    <div key={category}>
                      <h4 className="mb-3">{category}</h4>
                      <div className="space-y-2">
                        {permissions.map(permission => (
                          <div key={permission.id} className="flex items-start space-x-3 p-2 rounded hover:bg-muted/50">
                            <Checkbox
                              id={`${selectedRole}-${permission.id}`}
                              checked={hasRolePermission(selectedRole, permission.id)}
                              onCheckedChange={() => toggleRolePermission(selectedRole, permission.id)}
                              disabled={selectedRole === 'systemAdmin'} // System Admin always has all permissions
                            />
                            <div className="grid gap-1 leading-none flex-1">
                              <Label
                                htmlFor={`${selectedRole}-${permission.id}`}
                                className="cursor-pointer"
                              >
                                {permission.name}
                              </Label>
                              <p className="text-muted-foreground">
                                {permission.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="user" className="space-y-6 mt-6">
              <Alert>
                <AlertDescription>
                  User-specific permissions override role-based permissions. Set individual permissions for specific users that need different access levels.
                </AlertDescription>
              </Alert>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="user-select">Select User</Label>
                  <Select value={selectedUser} onValueChange={setSelectedUser}>
                    <SelectTrigger id="user-select">
                      <SelectValue placeholder="Choose a user to manage permissions" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.email} ({
                            user.companyName !== 'Unknown' 
                              ? user.companyName 
                              : user.firmName !== 'Unknown' 
                                ? user.firmName 
                                : user.role === 'systemAdmin' || user.role === 'system_admin'
                                  ? 'System Admin'
                                  : 'Unknown'
                          })
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedUser && (
                  <Card>
                    <CardHeader>
                      <CardTitle>
                        Individual Permissions: {users.find(u => u.id === selectedUser)?.email}
                      </CardTitle>
                      <CardDescription>
                        These permissions will override the default role permissions for this user
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {Object.entries(groupedPermissions).map(([category, permissions]) => (
                        <div key={category}>
                          <h4 className="mb-3">{category}</h4>
                          <div className="space-y-2">
                            {permissions.map(permission => (
                              <div key={permission.id} className="flex items-start space-x-3 p-2 rounded hover:bg-muted/50">
                                <Checkbox
                                  id={`${selectedUser}-${permission.id}`}
                                  checked={hasUserPermission(selectedUser, permission.id)}
                                  onCheckedChange={() => toggleUserPermission(selectedUser, permission.id)}
                                />
                                <div className="grid gap-1 leading-none flex-1">
                                  <Label
                                    htmlFor={`${selectedUser}-${permission.id}`}
                                    className="cursor-pointer"
                                  >
                                    {permission.name}
                                  </Label>
                                  <p className="text-muted-foreground">
                                    {permission.description}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}